<script setup>
  import { ref, computed, watchEffect, onMounted } from 'vue';
  import { useUserStore } from '@/stores/UserStore'
  import ModalFavoriteItemManager from '@/components/ModalFavoriteItemManager.vue';
  import ModalRenameFavoriteContainer from '@/components/ModalRenameFavoriteContainer.vue';
  import ModalDeleteFavoriteSpot from '@/components/ModalDeleteFavoriteSpot.vue';
  import ModalMoveCopyItemsToProfile from '@/components/ModalMoveCopyItemsToProfile.vue';

  const emits = defineEmits(['close', 'sortEnd']);

  const props = defineProps({
    spots: {
      type: Array,
      required: true  
    },
    show: {
      type: Boolean,
      default: false
    }
  })

  const userStore = useUserStore();

  const spots = ref(props.spots);
  const showModal = ref(false);
  const selectedSpotIds = ref([]);

  const selectedSpots = computed(() => props.spots.filter(spot => selectedSpotIds.value.includes(spot.fav_spot_id)));

  function closeModal() {
    showModal.value = false;

    emits('close');
  }

  async function handleSortEnd(items) {
    const newSpotOrder = items.map(item => item.fav_spot_id);
    const profileId = userStore.currentProfileId;
    await userStore.sortSpotsInProfile(profileId, newSpotOrder);
    await userStore.getProfiles(true);
    userStore.setCurrentProfile(profileId);
  } 
  

  onMounted(() => {
    // Placing the watchEffect in the onMounted callback makes sure
    // the BaseModal has time to run the modal open animation by delaying the 
    // showModal value being set to true until after this component mounts    
    watchEffect(() => showModal.value = props.show);
  });
  
  watchEffect(() => spots.value = props.spots);
</script>

<template>
  <ModalFavoriteItemManager
    v-if="showModal"
    v-model="selectedSpotIds"
    :items="spots"
    idField="fav_spot_id"
    nameField="name"
    title="Favorite Spots"
    :show="showModal"
    :hideActions="['rename']"
    :dragDisabled="userStore.favoriteSortOrder !== 'userdefined'"
    @sortEnd="handleSortEnd"
    @close="closeModal"
  >
    <template #description>
      <p class="jwx-modal__text">Click on the menu button to copy, move, or delete a favorited spot.</p>
      <p class="jwx-modal__text">To manage multiple spots at once, click on the "Select" button. Then select the spots you would like to manage.</p>
    </template>
    <template #modals="{ showRenameModal, showDeleteModal, showMoveCopyModal, closeSecondaryModals, moveCopyAction }">
      <ModalRenameFavoriteContainer
        v-if="showRenameModal"
        :show="showRenameModal"
        :containerId="selectedSpotIds[0]"
        @close="closeSecondaryModals"
      /> 
      <ModalDeleteFavoriteSpot 
        v-if="showDeleteModal"
        :show="showDeleteModal"
        :spotId="selectedSpotIds"
        :profileId="userStore.currentProfileId"
        @close="closeSecondaryModals"
      /> 
      <ModalMoveCopyItemsToProfile
        v-if="showMoveCopyModal"
        :items="selectedSpots"
        type="spot"
        :action="moveCopyAction"
        :show="showMoveCopyModal"
        @close="closeSecondaryModals"
      />
    </template>
  </ModalFavoriteItemManager>
</template>